/**
 * Favorite heart on the TIG shorts player modal.
 *
 * Carousel tiles live in a cross-origin iframe, so the heart can only go on the
 * player, which relays postMessage events to the parent:
 *   action: "openModal"           { identifier }            carousel tile click
 *   TigIframe#event#onPlay        { data: { content_id } }  My Page (customwapi)
 *   tig2embed.event.videoSwitched { data: { identifier } }  swipe
 *   TigEmbed#event#onModalClose
 *
 * content_id and identifier are the same value.
 *
 * Reads and writes go through tig-short-favorite-store.js. A logged-out click
 * goes through tig-short-favorite-login-redirect.js, which brings the customer
 * back to this short after the login page.
 */
(function () {
  if (window.__tigShortFavoriteInit) return;
  window.__tigShortFavoriteInit = true;

  var config = window.tigShortFavoriteConfig || {};
  var loggedIn = config.loggedIn === true;
  var BUTTON_ID = 'tig-short-favorite-btn';

  var store = window.TigShortFavoriteStore;
  if (!store) {
    console.warn('[tig-short-favorite] tig-short-favorite-store.js did not load');
  }

  function removeButton() {
    cancelPendingWait();
    var existing = document.getElementById(BUTTON_ID);
    if (existing) existing.remove();
  }

  // Visibility is the test, not the wrapper: the archive/live embed leaves its
  // own .tig-modal in the DOM permanently at visibility:hidden, and a heart
  // appended there inherits hidden and is never seen.
  function findModalContent() {
    var visible = [].slice.call(document.querySelectorAll('.tig-modal-content')).filter(
      function (content) {
        return getComputedStyle(content).visibility !== 'hidden';
      }
    );
    if (visible.length === 0) return null;
    var preferred = visible.filter(function (content) {
      return content.closest('.tig-creator-iframe-area');
    });
    return preferred[0] || visible[0];
  }

  var WAIT_TIMEOUT_MS = 5000;
  var pending = null;

  function waitForModalContent(contentId, callback) {
    // Several events fire per short. Restarting the wait on each also reset the
    // timeout, pushing the fallback out to double or triple its intended delay.
    if (pending && pending.contentId === contentId) return;
    cancelPendingWait();

    var content = findModalContent();
    if (content) return callback(content);

    var observer = new MutationObserver(function () {
      var found = findModalContent();
      if (!found) return;
      cancelPendingWait();
      callback(found);
    });
    observer.observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });

    var timeout = setTimeout(function () {
      cancelPendingWait();
      console.warn('[tig-short-favorite] player modal not found, falling back to body');
      callback(document.body);
    }, WAIT_TIMEOUT_MS);

    pending = { contentId: contentId, observer: observer, timeout: timeout };
  }

  function cancelPendingWait() {
    if (!pending) return;
    pending.observer.disconnect();
    clearTimeout(pending.timeout);
    pending = null;
  }

  function renderButton(contentId) {
    if (!contentId) return;

    var existing = document.getElementById(BUTTON_ID);
    if (existing && existing.isConnected && existing.dataset.contentId === contentId) {
      return;
    }

    waitForModalContent(contentId, function (container) {
      removeButton();

      var button = document.createElement('button');
      button.id = BUTTON_ID;
      button.type = 'button';
      button.className = 'tig-short-favorite-btn';
      if (container !== document.body) button.classList.add('is-anchored');
      button.setAttribute('aria-label', 'お気に入り');
      button.dataset.contentId = contentId;
      setActive(button, !!store && store.has(contentId));
      button.addEventListener('click', onToggle);
      container.appendChild(button);
    });
  }

  function toLogin(contentId) {
    var login = window.TigShortFavoriteLoginRedirect;
    if (login) return login.toLogin(contentId);
    window.location.href = '/account/login';
  }

  function setActive(button, isActive) {
    button.classList.toggle('is-active', isActive);
    button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
  }

  function onToggle(event) {
    event.preventDefault();
    var button = event.currentTarget;
    var contentId = button.dataset.contentId;
    var wasActive = button.classList.contains('is-active');

    // The write is per customer, so there is nothing to save while logged out.
    // The short rides across the login and the player is reopened on the way
    // back, see tig-short-favorite-login-redirect.js.
    if (!loggedIn) {
      toLogin(contentId);
      return;
    }
    if (!store || button.disabled) return;

    // Optimistic, reverted below if the request fails. Disabled meanwhile so a
    // double click cannot queue a contradictory second write.
    setActive(button, !wasActive);
    button.disabled = true;
    button.classList.add('heart-beat');
    setTimeout(function () {
      button.classList.remove('heart-beat');
    }, 1000);

    var written = wasActive ? store.remove(contentId) : store.add(contentId);
    written.then(function (result) {
      if (result.ok) {
        button.disabled = false;
        return;
      }
      // The list is full. Offer to free a slot rather than leaving the customer
      // with a heart that silently refuses to stay lit.
      if (result.limitReached && window.tigShortFavoriteLimitDialog) {
        return freeSlotAndRetry(button, contentId, wasActive);
      }
      button.disabled = false;
      setActive(button, wasActive);
    });
  }

  /** Drops the short the customer picked, then retries the favorite. */
  function freeSlotAndRetry(button, contentId, wasActive) {
    return window.tigShortFavoriteLimitDialog(store.ids()).then(function (chosen) {
      if (!chosen) {
        button.disabled = false;
        setActive(button, wasActive);
        return;
      }
      return store.remove(chosen).then(function (removed) {
        if (!removed.ok) {
          button.disabled = false;
          setActive(button, wasActive);
          return;
        }
        return store.add(contentId).then(function (added) {
          button.disabled = false;
          if (!added.ok) setActive(button, wasActive);
        });
      });
    });
  }

  window.addEventListener('message', function (event) {
    var message = event.data;
    if (!message || typeof message !== 'object') return;

    // Carousel tile click. Identified by `action`, it has no `type` field.
    if (message.action === 'openModal') {
      renderButton(message.identifier);
      return;
    }

    var payload = message.data || {};

    switch (message.type) {
      case 'TigIframe#event#onPlay':
        // Covers My Page, where customwapi means openModal never fires.
        if (!document.getElementById(BUTTON_ID)) {
          renderButton(payload.content_id || payload.identifier);
        }
        break;

      case 'tig2embed.event.videoSwitched':
        if (payload.identifier) renderButton(payload.identifier);
        break;

      case 'TigEmbed#event#onModalClose':
        removeButton();
        break;
    }
  });
})();
